import { Download } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { triggerBlobDownload } from "@/utils";

import { type TColumnDef } from "./types";
import { rowsToCsv, rowsToJson } from "./utils";

type TDataGridExportProps<TRow> = {
  formats: ("csv" | "json")[];
  columns: TColumnDef<TRow>[];
  /** Rows currently visible on the page. */
  currentRows: TRow[];
  /** Fetch all filtered rows (server mode) or the full filtered set (client). */
  getAllRows: () => Promise<TRow[]>;
  filenameBase: string;
};

/** Export menu: CSV/JSON of the current page or all filtered rows (client-side). */
export function DataGridExport<TRow>({
  formats,
  columns,
  currentRows,
  getAllRows,
  filenameBase,
}: TDataGridExportProps<TRow>) {
  const exportRows = (rows: TRow[], format: "csv" | "json") => {
    const content = format === "csv" ? rowsToCsv(columns, rows) : rowsToJson(columns, rows);
    const mime = format === "csv" ? "text/csv;charset=utf-8" : "application/json";
    const blob = new Blob([content], { type: mime });
    triggerBlobDownload(blob, `${filenameBase}.${format}`);
  };

  const handleAll = async (format: "csv" | "json") => {
    const rows = await getAllRows();
    exportRows(rows, format);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Export data"
        >
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Current page</DropdownMenuLabel>
        {formats.map((format) => (
          <DropdownMenuItem
            key={`page-${format}`}
            onClick={() => exportRows(currentRows, format)}
          >
            Export page as {format.toUpperCase()}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>All filtered rows</DropdownMenuLabel>
        {formats.map((format) => (
          <DropdownMenuItem
            key={`all-${format}`}
            onClick={() => void handleAll(format)}
          >
            Export all as {format.toUpperCase()}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
